import { ConfigService } from '@axo/config/ConfigService';
import { DateFieldUI } from '@axo/form/feature/form-input/DateField/DateFieldUI';
import { TextFieldUI } from '@axo/form/feature/form-input/TextField/TextFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBroker } from '../../../../../../hooks/useBroker';
import type { EmploymentFormGroupData } from '../../../../FormData.fi.types';
import { gridColumns } from '../../../config/layouts/grid';
import {
  EmployerIndustryField,
  EmploymentStatusField,
  JobPositionField,
} from '../../../fields';
import styles from '../../../loan.module.scss';
import {
  employerFieldOption,
  employmentStatusSinceFieldOption,
  employmentStatusUntilFieldOption,
  jobTitleFieldOption,
} from './config';
import { getEmploymentStatusVisibility } from './getEmploymentStatusVisibility';

export function EmploymentFormGroup() {
  const { t } = useTranslation();

  const broker = useBroker();
  const { hasEmployerIndustry, hasJobPosition } = ConfigService.get(
    'product-unsecured-loan'
  ).fields[broker];

  const currentYear = new Date().getFullYear();

  const { watch } = useFormContext();

  const employmentStatus = watch('LoanApplication/EmploymentStatus');

  const {
    askEmploymentDetails,
    askEmploymentStatusSince,
    askEmploymentStatusUntil,
    askJobPosition,
  } = useMemo(
    () =>
      getEmploymentStatusVisibility({
        employmentStatus,
        broker,
        hasJobPosition,
      }),
    [broker, employmentStatus, hasJobPosition]
  );

  return (
    <div className={styles.inputGroup} data-grey={true}>
      <Grid columns={gridColumns}>
        <EmploymentStatusField />

        {askEmploymentDetails && (
          <TextFieldUI<EmploymentFormGroupData>
            name="LoanApplication/Employer"
            label={t('Employer.label')}
            data-testid="employer-input"
            {...employerFieldOption}
          />
        )}

        {askEmploymentDetails && hasEmployerIndustry && (
          <EmployerIndustryField />
        )}

        {askJobPosition && <JobPositionField />}

        {askEmploymentStatusSince && (
          <div className={styles.dateFieldUI}>
            <DateFieldUI<EmploymentFormGroupData>
              name="LoanApplication/EmploymentStatusSince"
              label={t('EmploymentStatusSince.label', {
                employmentStatus: t(`EmploymentStatus.${employmentStatus}`),
              })}
              data-testid="employment-status-since-input"
              minYear={currentYear - 10}
              maxYear={currentYear}
              disableDatesInFuture
              minYearSuffix={t('EmploymentStatusSince.year.suffix')}
              yearPlaceholder={t('EmploymentStatusSince.placeholder.year')}
              monthPlaceholder={t('EmploymentStatusSince.placeholder.month')}
              latestYearAtTop={true}
              i18n="fi-FI"
              rules={employmentStatusSinceFieldOption.registerOptions}
            />
          </div>
        )}

        {askEmploymentStatusUntil && (
          <div className={styles.dateFieldUI}>
            <DateFieldUI<EmploymentFormGroupData>
              name="LoanApplication/EmploymentStatusUntil"
              label={t('EmploymentStatusUntil.label')}
              data-testid="employment-status-until-input"
              minYear={currentYear}
              maxYear={currentYear + 10}
              maxYearSuffix={t('EmploymentStatusUntil.year.suffix')}
              yearPlaceholder={t('EmploymentStatusUntil.placeholder.year')}
              monthPlaceholder={t('EmploymentStatusUntil.placeholder.month')}
              latestYearAtTop={true}
              i18n="fi-FI"
              disableDatesInPast
              rules={employmentStatusUntilFieldOption.registerOptions}
            />
          </div>
        )}

        {askEmploymentDetails && (
          <TextFieldUI<EmploymentFormGroupData>
            name="LoanApplication/JobTitle"
            label={t('JobTitle.label')}
            data-testid="job-title-input"
            {...jobTitleFieldOption}
          />
        )}
      </Grid>
    </div>
  );
}
